// Canonicalización de leadId (Fase — fix links "ver el lead" HTTP 400).
//
// El CRM devuelve `leadId` en su formato INTERNO hex-32 minúscula en varias
// respuestas (GET /phone/calls, GET /tickets, GET /tickets/board, GET
// /tickets/:id). El admin-api de Operations
// (/api/admin/operations/leads/:leadUlid/detail) SOLO acepta el ULID canónico
// Crockford-26 → un link construido con hex da 400. Normalizamos en la CAPA API
// (no en cada vista) para que ninguna vista vuelva a linkar con hex.
//
// Transcripción de crm/packages/contracts/src/ulid.ts + topics.ts (los dos
// repos NO comparten paquete). Los helpers son TOLERANTES: canónico → tal cual,
// null/undefined → null, formato desconocido → sin tocar (no rompemos nada).

const HEX_ULID_RE = /^[0-9a-f]{32}$/;
const CANONICAL_ULID_RE = /^[0-9A-HJKMNP-TV-Z]{26}$/;
const CROCKFORD = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";

/**
 * Convierte un ULID hex (32 chars lowercase) a su forma canónica Crockford
 * base32 (26 chars uppercase). Lanza si el input no es hex-32.
 */
export function hexUlidToCanonical(hex: string): string {
  const normalized = hex.toLowerCase();
  if (!HEX_ULID_RE.test(normalized)) {
    throw new Error(`hexUlidToCanonical: not a 32-char hex string: ${hex}`);
  }
  let value = 0n;
  for (let i = 0; i < 32; i += 2) {
    value = (value << 8n) | BigInt(parseInt(normalized.slice(i, i + 2), 16));
  }
  let out = "";
  for (let i = 25; i >= 0; i--) {
    out += CROCKFORD[Number((value >> BigInt(i * 5)) & 0x1fn)];
  }
  return out;
}

/**
 * Canonicaliza el leadId a Crockford-26. hex-32 → Crockford; un canónico se
 * devuelve idéntico; cualquier otro formato se deja sin tocar (no romper).
 */
export function canonicalLeadId(id: string): string {
  if (HEX_ULID_RE.test(id.toLowerCase())) return hexUlidToCanonical(id);
  if (CANONICAL_ULID_RE.test(id)) return id;
  return id;
}

/**
 * Variante tolerante a null/undefined para normalizar campos opcionales de las
 * respuestas de la capa API sin ensuciar cada call-site con guards.
 */
export function canonicalLeadIdOrNull(
  id: string | null | undefined,
): string | null {
  if (id == null || id === "") return null;
  return canonicalLeadId(id);
}

if (process.env.NODE_ENV !== "production") {
  // Test de forma inline (no hay jest en el SPA): un hex-32 conocido debe dar un
  // Crockford-26 mayúsculas; un canónico se conserva. Falla ruidoso en dev si la
  // transcripción diverge del contrato del CRM.
  const HEX = "0190b3a1c2d34e5f6a7b8c9d0e1f2a3b";
  const canon = canonicalLeadId(HEX);
  if (!CANONICAL_ULID_RE.test(canon)) {
    // eslint-disable-next-line no-console
    console.error("[crmLeadId] canonicalLeadId hex→Crockford roto:", canon);
  }
  if (
    canonicalLeadId("01ARZ3NDEKTSV4RRFFQ69G5FAV") !==
    "01ARZ3NDEKTSV4RRFFQ69G5FAV"
  ) {
    // eslint-disable-next-line no-console
    console.error("[crmLeadId] canonicalLeadId no conserva un canónico válido");
  }
}
